/**
 * Authentication state of the application.
 *
 *  - boot: silent `POST /auth/refresh` (cookie) → `GET /auth/me`
 *  - login flows return the raw {@link LoginResponse}; the page decides when to `completeLogin`
 *    (e.g. after showing MFA recovery codes)
 *  - permissions from `/auth/me` drive `usePermission`, `<Can>` and `<RequirePermission>`
 *  - logout / user switch wipes client state (SEC-10)
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiConfig, refreshAccessToken } from '@web/lib/api';
import { tokenStore } from '@web/lib/tokenStore';
import { realtime } from '@web/lib/realtime';
import { currentLanguage, setLanguage, storedLanguage } from '@web/i18n';
import { hasPermission as matchPermission, type PermissionRequirement } from './permissions';
import { resetClientState } from './session';
import type { AuthUser, LoginOk, LoginResponse, MeResponse, RoleGrantSummary } from './types';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  permissions: ReadonlySet<string>;
  roles: RoleGrantSummary[];
  portals: string[];
}

export interface AuthContextValue extends AuthState {
  /** `POST /auth/login`. */
  login(login: string, password: string): Promise<LoginResponse>;
  verifyMfa(challengeToken: string, code: string): Promise<LoginResponse>;
  enrollMfa(enrollToken: string, code: string): Promise<LoginResponse>;
  changeRequiredPassword(changeToken: string, newPassword: string): Promise<LoginResponse>;
  /** Stores the access token, loads `/auth/me` and switches to the user's locale. */
  completeLogin(ok: LoginOk): Promise<void>;
  logout(reason?: 'user' | 'idle' | 'switch'): Promise<void>;
  reloadMe(): Promise<void>;
  can(perm: PermissionRequirement | undefined | null, mode?: 'any' | 'all'): boolean;
  /** Display name in the current language. */
  displayName: string;
}

const ANON: AuthState = { status: 'anonymous', user: null, permissions: new Set(), roles: [], portals: [] };

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState<AuthState>({ ...ANON, status: 'loading' });
  const userRef = useRef<AuthUser | null>(null);
  userRef.current = state.user;

  const loadMe = useCallback(async (): Promise<MeResponse> => {
    const me = await api.get<MeResponse>('/auth/me');
    setState({
      status: 'authenticated',
      user: me.user,
      permissions: new Set(me.permissions ?? []),
      roles: me.roles ?? [],
      portals: me.portals ?? [],
    });
    return me;
  }, []);

  const toAnonymous = useCallback(() => {
    tokenStore.clear();
    realtime.stop();
    resetClientState();
    setState(ANON);
  }, []);

  // a failed refresh (session ended elsewhere) signs the user out
  useEffect(() => {
    apiConfig.setAuthFailureHandler(() => {
      if (userRef.current) toAnonymous();
    });
    return () => {
      apiConfig.setAuthFailureHandler(null);
    };
  }, [toAnonymous]);

  // boot: silent refresh
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = await refreshAccessToken();
      if (cancelled) return;
      if (!ok) {
        setState(ANON);
        return;
      }
      try {
        await loadMe();
      } catch {
        if (!cancelled) setState(ANON);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  const completeLogin = useCallback(
    async (ok: LoginOk) => {
      resetClientState(); // never show data cached for a previous user
      tokenStore.set(ok.accessToken, ok.expiresIn);
      const me = await loadMe();
      const locale = me.user.locale ?? ok.user.locale;
      // the user's language applies unless this browser has an explicit language choice
      if ((locale === 'ar' || locale === 'en') && !storedLanguage() && locale !== currentLanguage()) {
        await setLanguage(locale, false);
      }
    },
    [loadMe],
  );

  const logout = useCallback(
    async (_reason: 'user' | 'idle' | 'switch' = 'user') => {
      try {
        await api.post('/auth/logout', undefined, { retryOn401: false });
      } catch {
        /* best effort */
      }
      toAnonymous();
    },
    [toAnonymous],
  );

  const value = useMemo<AuthContextValue>(() => {
    const u = state.user;
    return {
      ...state,
      login: (login, password) => api.post<LoginResponse>('/auth/login', { login, password, portal: 'ADMIN' }),
      verifyMfa: (challengeToken, code) => api.post<LoginResponse>('/auth/mfa/verify', { challengeToken, code }),
      enrollMfa: (enrollToken, code) => api.post<LoginResponse>('/auth/mfa/enroll', { enrollToken, code }),
      changeRequiredPassword: (changeToken, newPassword) =>
        api.post<LoginResponse>('/auth/password/change-required', { changeToken, newPassword }),
      completeLogin,
      logout,
      reloadMe: async () => {
        await loadMe();
      },
      can: (perm, mode) => matchPermission(state.permissions, perm, mode),
      displayName: u ? (lang === 'en' ? u.displayNameEn || u.displayNameAr : u.displayNameAr || u.displayNameEn || '') : '',
    };
  }, [state, lang, completeLogin, logout, loadMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * `usePermission('iam.user.create')`, `usePermission(['a','b'])` (any), `usePermission('core.customer.*')`.
 */
export function usePermission(perm: PermissionRequirement | undefined | null, mode: 'any' | 'all' = 'any'): boolean {
  const { permissions } = useAuth();
  return matchPermission(permissions, perm, mode);
}
